/**
 * 백엔드 `ErrorCode` enum(KioSchool-API #17) 미러.
 *
 * 백엔드는 모든 에러 응답 바디에 기계 판독용 `code`를 담아 내려준다.
 * - 비즈니스 예외 → 아래 enum 이름 (`NOT_SELLABLE_PRODUCT` 등)
 * - Spring MVC 프레임워크 예외 → HTTP status 이름 (415 → `UNSUPPORTED_MEDIA_TYPE` 등)
 *
 * 따라서 여기 없는 code는 프레임워크나 게이트웨이가 만든 것이며, 곧 프론트 요청
 * 자체가 잘못됐다는 신호다. 주석의 숫자는 함께 내려오는 HTTP status로, 참고용이며
 * 프론트 분기에는 쓰지 않는다. (status 하나에 성격이 다른 code가 겹치기 때문)
 */
export const API_ERROR_CODES = {
  // Common
  INVALID_INPUT: 'INVALID_INPUT', // 400
  INTERNAL_ERROR: 'INTERNAL_ERROR', // 500

  // User
  USER_NOT_FOUND: 'USER_NOT_FOUND', // 404
  LOGIN_FAILED: 'LOGIN_FAILED', // 401
  NO_PERMISSION: 'NO_PERMISSION', // 401
  DUPLICATE_LOGIN_ID: 'DUPLICATE_LOGIN_ID', // 400
  DUPLICATE_EMAIL: 'DUPLICATE_EMAIL', // 400
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED', // 400

  // Workspace
  WORKSPACE_INACCESSIBLE: 'WORKSPACE_INACCESSIBLE', // 405
  WORKSPACE_NOT_FOUND: 'WORKSPACE_NOT_FOUND', // 404
  WORKSPACE_TABLE_NOT_FOUND: 'WORKSPACE_TABLE_NOT_FOUND', // 404
  TABLE_POSITION_CONFLICT: 'TABLE_POSITION_CONFLICT', // 409
  INVALID_TABLE_POSITION: 'INVALID_TABLE_POSITION', // 400
  NO_PERMISSION_TO_CREATE_WORKSPACE: 'NO_PERMISSION_TO_CREATE_WORKSPACE', // 401
  NO_PERMISSION_TO_INVITE: 'NO_PERMISSION_TO_INVITE', // 403
  NO_PERMISSION_TO_JOIN_WORKSPACE: 'NO_PERMISSION_TO_JOIN_WORKSPACE', // 403
  SUPER_ADMIN_WORKSPACE_READ_ONLY: 'SUPER_ADMIN_WORKSPACE_READ_ONLY', // 400

  // Product
  NOT_FOUND_PRODUCT: 'NOT_FOUND_PRODUCT', // 404
  NOT_SELLABLE_PRODUCT: 'NOT_SELLABLE_PRODUCT', // 406
  CANNOT_DELETE_USING_PRODUCT_CATEGORY: 'CANNOT_DELETE_USING_PRODUCT_CATEGORY', // 405

  // Order
  EMPTY_ORDER_SESSION: 'EMPTY_ORDER_SESSION', // 400
  NO_ORDER_SESSION: 'NO_ORDER_SESSION', // 400
  ORDER_SESSION_ALREADY_EXIST: 'ORDER_SESSION_ALREADY_EXIST', // 400
  TABLE_HASH_IS_NULL: 'TABLE_HASH_IS_NULL', // 400

  // Account / Toss
  BANK_NOT_FOUND: 'BANK_NOT_FOUND', // 404
  BANK_TOSS_NAME_NOT_FOUND: 'BANK_TOSS_NAME_NOT_FOUND', // 400
  ACCOUNT_HOLDER_NOT_FOUND: 'ACCOUNT_HOLDER_NOT_FOUND', // 404
  DIFFERENT_ACCOUNT_NUMBER: 'DIFFERENT_ACCOUNT_NUMBER', // 400

  // Email
  DUPLICATE_EMAIL_DOMAIN: 'DUPLICATE_EMAIL_DOMAIN', // 400
  NOT_VERIFIED_EMAIL_DOMAIN: 'NOT_VERIFIED_EMAIL_DOMAIN', // 422
  EMAIL_SEND_FAILURE: 'EMAIL_SEND_FAILURE', // 500

  // Inquiry
  INQUIRY_NOT_FOUND: 'INQUIRY_NOT_FOUND', // 404
  INQUIRY_ALREADY_ANSWERED: 'INQUIRY_ALREADY_ANSWERED', // 409
  INQUIRY_ALREADY_CLOSED: 'INQUIRY_ALREADY_CLOSED', // 409
  INQUIRY_IMAGE_LIMIT_EXCEEDED: 'INQUIRY_IMAGE_LIMIT_EXCEEDED', // 400
  INQUIRY_IMAGE_TOO_LARGE: 'INQUIRY_IMAGE_TOO_LARGE', // 413
  INQUIRY_IMAGE_TYPE_NOT_ALLOWED: 'INQUIRY_IMAGE_TYPE_NOT_ALLOWED', // 415
  INQUIRY_RATE_LIMIT_EXCEEDED: 'INQUIRY_RATE_LIMIT_EXCEEDED', // 429

  // Security
  AUTHENTICATION_REQUIRED: 'AUTHENTICATION_REQUIRED', // 401
  ACCESS_DENIED: 'ACCESS_DENIED', // 403
  INVALID_JWT: 'INVALID_JWT', // 401
  CAPTCHA_VERIFICATION_FAILED: 'CAPTCHA_VERIFICATION_FAILED', // 400
} as const;

export type ApiErrorCode = typeof API_ERROR_CODES[keyof typeof API_ERROR_CODES];

/**
 * 세션 자체가 무효임을 뜻하는 code. 글로벌 로그아웃 이벤트를 발행한다.
 *
 * - `AUTHENTICATION_REQUIRED`: 쿠키 부재·만료 토큰. JWT 필터는 이 경우 예외를 던지지 않고
 *   통과시키므로, Spring Security의 `CustomAuthenticationEntryPoint`가 code를 실어준다.
 * - `INVALID_JWT`: 토큰은 유효하나 해당 사용자가 삭제된 경우.
 *
 * `ACCESS_DENIED`(403)는 인증은 됐고 권한만 부족한 상태라 세션이 유효하므로 제외한다.
 */
export const SESSION_INVALID_ERROR_CODES: readonly ApiErrorCode[] = [API_ERROR_CODES.AUTHENTICATION_REQUIRED, API_ERROR_CODES.INVALID_JWT];
